// src/features/admin-options/components/AdminSectionNav.tsx

import React from 'react';
import {
  Box,
  FormControl,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import type { SubMenuItem } from '@/components/common/SubMenu';

interface AdminSectionNavProps {
  items: SubMenuItem[];
  activeItem: string;
  onChange: (value: string) => void;
}

const NAV_WIDTH = 232;

const withIconSize = (icon: React.ReactNode, color?: string) =>
  React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ sx?: object }>, { sx: { fontSize: 18, color: color ?? 'inherit' } })
    : null;

/**
 * Secondary navigation of the admin options page.
 * md and up: slim vertical list on the left of the content.
 * Below md: one compact select above the content.
 */
export const AdminSectionNav: React.FC<AdminSectionNavProps> = ({ items, activeItem, onChange }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const label = translate('resources.adminOptions.nav.label');

  if (items.length <= 1) return null;

  if (isCompact) {
    return (
      <Box component="nav" aria-label={label} sx={{ mb: 1.5 }}>
        <FormControl size="small" fullWidth>
          <Select
            value={activeItem}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{ 'aria-label': label }}
            renderValue={(value) => {
              const item = items.find((i) => i.value === value);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {withIconSize(item?.icon, theme.palette.primary.main)}
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {translate('resources.adminOptions.nav.current')}
                  </Box>
                  <Box component="span" sx={{ fontWeight: 600 }}>{item?.label}</Box>
                </Box>
              );
            }}
            sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.75 } }}
          >
            {items.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                <ListItemIcon sx={{ minWidth: 28 }}>{withIconSize(item.icon)}</ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box
      component="nav"
      aria-label={label}
      sx={{ width: NAV_WIDTH, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 0 }}
    >
      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {items.map((item) => {
          const selected = item.value === activeItem;
          return (
            <ListItemButton
              key={item.value}
              selected={selected}
              aria-current={selected ? 'page' : undefined}
              onClick={() => onChange(item.value)}
              sx={{
                minHeight: 36,
                py: 0.5,
                px: 1.25,
                borderRadius: 1,
                color: selected ? 'text.primary' : 'text.secondary',
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: selected ? 'primary.main' : 'inherit' }}>
                {withIconSize(item.icon)}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: selected ? 600 : 500, lineHeight: 1.3 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default AdminSectionNav;
